import React, { useMemo } from "react";
import { cn } from "@/modern/utils";
import {
  getAlwaysVisibleKanbanFields,
  getCatalogFieldById,
  getKanbanCardFieldCatalog,
  type KanbanCardFieldRenderContext,
} from "@/modern/constants/kanbanFieldUtils";
import type { KanbanCardFieldDefinition } from "@/modern/constants/kanbanCardFieldCatalog";
import "@/modern/constants/kanbanCardFieldCatalog";

export type KanbanCardContentProps = {
  entityType: string;
  record: any;
  visibleFieldIds: string[];
  project?: any;
  userPreferences?: KanbanCardFieldRenderContext["userPreferences"];
  grouperField?: string;
  className?: string;
};

export function KanbanCardContent({
  entityType,
  record,
  visibleFieldIds,
  project,
  userPreferences = null,
  grouperField,
  className,
}: KanbanCardContentProps) {
  const catalog = useMemo(() => getKanbanCardFieldCatalog(entityType), [entityType]);

  const renderCtx: Omit<KanbanCardFieldRenderContext, "field"> = useMemo(
    () => ({
      entityType,
      record,
      project,
      userPreferences,
      grouperField,
    }),
    [entityType, record, project, userPreferences, grouperField]
  );

  const alwaysVisibleFields = useMemo(() => getAlwaysVisibleKanbanFields(catalog), [catalog]);

  const fieldCtx = (field: KanbanCardFieldDefinition): KanbanCardFieldRenderContext => ({
    ...renderCtx,
    field,
  });

  return (
    <div className={cn("relative flex min-w-0 flex-1 flex-col gap-2.5 text-sm", className)}>
      {alwaysVisibleFields.map((field) => (
        <React.Fragment key={field.id}>{field.renderer(fieldCtx(field))}</React.Fragment>
      ))}
      {visibleFieldIds.map((fieldId) => {
        const field = getCatalogFieldById(entityType, fieldId);
        if (!field || field.alwaysVisible) return null;
        const content = field.renderer(fieldCtx(field));
        if (!content) return null;
        return <React.Fragment key={fieldId}>{content}</React.Fragment>;
      })}
    </div>
  );
}
